/**
 * PackageHistory — Ted #1 (#904).
 *
 * "In Gingr, if we discover that we missed deducting a daycare visit from a
 * customer's package, particularly something that happened in the past, we
 * can go into the client's History, select the package … manually deduct the
 * visit, and enter a note explaining the reason."
 *
 * Under each package on the customer profile: the ledger (every change to
 * the balance — which visit, how many days, why, who) and, for staff with
 * the desk write permission, **Correct balance**: deduct a day for a past
 * visit that was charged when it should have used one, or restore a day
 * that was used when it should not have been. The visit date may be in the
 * past (never the future) and a reason is required; the server writes the
 * ledger row in the same transaction as the counter change.
 */
import { useCallback, useEffect, useState, type FormEvent, type JSX } from 'react';
import { Alert } from '@ferro/ui';
import {
  adminPaymentsApi,
  type AdminAdjustPackageInput,
  type AdminPackageLedgerEntry,
} from '../../lib/api.js';
import type { CustomerPackage } from './PackageBalance.js';
// #1157 E2.6 — Add days / Expiry, beside Correct balance.
import { PackageTopUp } from './PackageTopUp.js';
import { packageKindForService } from './package-days.js';
import './PackageHistory.css';

/** A past visit the desk can name on a correction (from the customer's activity feed). */
export interface PackageHistoryVisit {
  readonly id: string;
  /** ISO date-time of the visit. */
  readonly date: string;
  readonly serviceName: string | null;
  readonly status: string;
}

export interface PackageHistoryProps {
  readonly apiUrl: string;
  readonly pkg: CustomerPackage;
  /** `payments-ops:write:all` — without it the history is read-only. */
  readonly canWrite: boolean;
  /** Past visits of this customer, newest first; the picker fills the date. */
  readonly visits?: ReadonlyArray<PackageHistoryVisit>;
  /** Called after a successful correction so the balance card can refresh. */
  readonly onChanged?: () => void | Promise<void>;
}

type Direction = AdminAdjustPackageInput['direction'];

function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** `YYYY-MM-DD` (a calendar day, no timezone) → "Sep 4, 2026". */
export function formatDay(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * #1157 — the ledger kinds the server can send, now all in
 * `AdminPackageLedgerKind` (lib/api.ts): `migration` (E2.1, the opening
 * balance carried over from Gingr), `manual_add` and `expiry_change` (E2.6).
 * Kept as an alias so callers and tests that import it still compile.
 */
export type PackageLedgerKindOnWire = AdminPackageLedgerEntry['kind'];
export type PackageLedgerRow = Omit<AdminPackageLedgerEntry, 'kind'> & {
  kind: PackageLedgerKindOnWire;
};

function dayWord(n: number): string {
  return `${n} day${n === 1 ? '' : 's'}`;
}

/** Plain words for the row: what happened and to which visit. */
export function describeEntry(e: PackageLedgerRow): string {
  const visit = e.serviceDate ? ` · visit ${formatDay(e.serviceDate)}` : '';
  switch (e.kind) {
    case 'checkout':
      return `Used at checkout${visit}`;
    case 'manual_deduct':
      return `Deducted by staff${visit}`;
    case 'manual_restore':
      return `Restored by staff${visit}`;
    case 'migration': {
      const used = Math.abs(e.deltaDays);
      return used > 0
        ? `Carried over from Gingr · ${dayWord(used)} already used there`
        : 'Carried over from Gingr';
    }
    // #1157 E2.6 — days added (the package grew), and the expiry changed.
    case 'manual_add':
      return 'Days added by staff';
    case 'expiry_change':
      return e.serviceDate ? `Expiry set to ${formatDay(e.serviceDate)}` : 'Expiry removed';
    default: {
      // A kind this build does not know yet (a newer server): say the
      // balance changed rather than render nothing.
      const unknown: never = e.kind;
      void unknown;
      return 'Balance changed';
    }
  }
}

/**
 * The left-hand figure of a row: the change in days; for a carried-over
 * balance, what was left; for an expiry change, "Expiry".
 */
export function entryFigure(e: PackageLedgerRow): string {
  if (e.kind === 'migration') return `${dayWord(e.remainingAfter)} left`;
  if (e.kind === 'expiry_change') return 'Expiry';
  return `${e.deltaDays > 0 ? '+' : '−'}${dayWord(Math.abs(e.deltaDays))}`;
}

/** Matching-service visits first, then the rest; only visits that already happened. */
export function orderVisits(
  visits: ReadonlyArray<PackageHistoryVisit>,
  serviceKind: string,
): PackageHistoryVisit[] {
  const today = todayIso();
  const past = visits.filter((v) => v.date.slice(0, 10) <= today);
  // #1157 — by package kind, not a substring: 'daycare_half' appears in no
  // service name, and a full-day package must not list half-day visits first.
  const matches = (v: PackageHistoryVisit) => packageKindForService(v.serviceName) === serviceKind;
  return [...past.filter(matches), ...past.filter((v) => !matches(v))].slice(0, 30);
}

export function PackageHistory({
  apiUrl,
  pkg,
  canWrite,
  visits = [],
  onChanged,
}: PackageHistoryProps): JSX.Element {
  const suffix = `-${pkg.id}`;
  const [entries, setEntries] = useState<PackageLedgerRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const res = await adminPaymentsApi.listPackageHistory(apiUrl, pkg.id);
    // Deploy-order safety: frontends ship before the server, so an older
    // server answers this route with a 404 (or a body without `entries`).
    // Render an honest "not available" line — never crash the profile.
    if (res.error || !res.data || !Array.isArray(res.data.entries)) {
      setLoadError(res.error ?? 'History is not available yet.');
      setEntries([]);
      return;
    }
    setLoadError(null);
    setEntries(res.data.entries);
  }, [apiUrl, pkg.id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // ── Correct balance ────────────────────────────────────────────────────
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState<Direction>('deduct');
  const [days, setDays] = useState('1');
  const [visitId, setVisitId] = useState('');
  const [serviceDate, setServiceDate] = useState(todayIso());
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  const orderedVisits = orderVisits(visits, pkg.serviceKind);

  const reset = () => {
    setDirection('deduct');
    setDays('1');
    setVisitId('');
    setServiceDate(todayIso());
    setReason('');
    setError(null);
  };

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();
      if (submitting) return;
      const n = Number.parseInt(days, 10);
      if (!Number.isFinite(n) || n <= 0) {
        setError('Enter a positive number of days.');
        return;
      }
      if (reason.trim().length < 2) {
        setError('Say why the balance is being corrected — the note is what the history shows.');
        return;
      }
      if (!serviceDate) {
        setError('Enter the date of the visit this correction is for.');
        return;
      }
      setSubmitting(true);
      setError(null);
      const res = await adminPaymentsApi.adjustPackage(apiUrl, pkg.id, {
        direction,
        days: n,
        serviceDate,
        reason: reason.trim(),
        ...(visitId ? { bookingId: visitId } : {}),
      });
      setSubmitting(false);
      if (res.error || !res.data) {
        setError(res.error ?? 'Could not correct the balance. Nothing was changed.');
        return;
      }
      const after = res.data.package.remainingDays;
      setConfirmation(
        `${direction === 'deduct' ? 'Deducted' : 'Restored'} ${dayWord(n)} — ${dayWord(after)} remaining.`,
      );
      setOpen(false);
      reset();
      await reload();
      if (onChanged) await onChanged();
    },
    [apiUrl, days, direction, onChanged, pkg.id, reason, reload, serviceDate, submitting, visitId],
  );

  return (
    <div className="ferro-package-history" data-testid={`package-history${suffix}`}>
      <div className="ferro-package-history__head">
        <span className="ferro-package-history__title">History</span>
        {canWrite ? (
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost ferro-package-history__toggle"
            onClick={() => {
              setOpen((v) => !v);
              setError(null);
              setConfirmation(null);
            }}
            aria-expanded={open}
            data-testid={`package-adjust-toggle${suffix}`}
          >
            {open ? 'Cancel' : 'Correct balance'}
          </button>
        ) : null}
      </div>
      {canWrite ? (
        <PackageTopUp
          apiUrl={apiUrl}
          pkg={pkg}
          onDone={async (message) => {
            setConfirmation(message);
            await reload();
            if (onChanged) await onChanged();
          }}
        />
      ) : null}

      {confirmation ? (
        <Alert variant="success">
          <span data-testid={`package-adjust-done${suffix}`}>{confirmation}</span>
        </Alert>
      ) : null}

      {open ? (
        <form
          className="ferro-form ferro-stack ferro-package-history__form"
          onSubmit={(e) => void handleSubmit(e)}
          data-testid={`package-adjust-form${suffix}`}
        >
          {error ? (
            <Alert variant="error">
              <span data-testid={`package-adjust-error${suffix}`}>{error}</span>
            </Alert>
          ) : null}
          <fieldset className="ferro-field ferro-package-history__direction">
            <legend className="ferro-field__label">What happened?</legend>
            <label className="ferro-package-history__option">
              <input
                type="radio"
                name={`package-adjust-direction${suffix}`}
                value="deduct"
                checked={direction === 'deduct'}
                onChange={() => setDirection('deduct')}
                data-testid={`package-adjust-deduct${suffix}`}
              />
              <span>A visit was charged that should have used a package day — deduct it now</span>
            </label>
            <label className="ferro-package-history__option">
              <input
                type="radio"
                name={`package-adjust-direction${suffix}`}
                value="restore"
                checked={direction === 'restore'}
                onChange={() => setDirection('restore')}
                data-testid={`package-adjust-restore${suffix}`}
              />
              <span>A day was used that should not have been — put it back</span>
            </label>
          </fieldset>
          <label className="ferro-field">
            <span className="ferro-field__label">Days</span>
            <input
              className="ferro-field__input ferro-package-history__days"
              type="number"
              min={1}
              max={365}
              inputMode="numeric"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              data-testid={`package-adjust-days${suffix}`}
            />
          </label>
          {orderedVisits.length > 0 ? (
            <label className="ferro-field">
              <span className="ferro-field__label">Which visit (optional)</span>
              <select
                className="ferro-field__input"
                value={visitId}
                onChange={(e) => {
                  const id = e.target.value;
                  setVisitId(id);
                  const v = orderedVisits.find((x) => x.id === id);
                  if (v) setServiceDate(v.date.slice(0, 10));
                }}
                data-testid={`package-adjust-visit${suffix}`}
              >
                <option value="">Not tied to a visit</option>
                {orderedVisits.map((v) => (
                  <option key={v.id} value={v.id}>
                    {formatDay(v.date)} · {v.serviceName ?? 'visit'} · {v.status.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="ferro-field">
            <span className="ferro-field__label">Visit date</span>
            <input
              className="ferro-field__input ferro-package-history__date"
              type="date"
              max={todayIso()}
              value={serviceDate}
              onChange={(e) => setServiceDate(e.target.value)}
              data-testid={`package-adjust-date${suffix}`}
            />
          </label>
          <label className="ferro-field">
            <span className="ferro-field__label">Reason (required)</span>
            <input
              className="ferro-field__input"
              type="text"
              maxLength={200}
              value={reason}
              placeholder="e.g. daycare on Sep 4 was charged instead of using a package day"
              onChange={(e) => setReason(e.target.value)}
              data-testid={`package-adjust-reason${suffix}`}
            />
          </label>
          <button
            type="submit"
            className="ferro-btn ferro-btn--primary"
            disabled={submitting}
            data-testid={`package-adjust-submit${suffix}`}
          >
            {submitting
              ? 'Saving…'
              : direction === 'deduct'
                ? 'Deduct from package'
                : 'Restore to package'}
          </button>
        </form>
      ) : null}

      {loadError ? (
        <p className="ferro-muted ferro-package-history__empty" data-testid={`package-history-unavailable${suffix}`}>
          {loadError}
        </p>
      ) : null}
      {entries === null ? (
        <p className="ferro-muted ferro-package-history__empty">Loading history…</p>
      ) : entries.length === 0 ? (
        <p className="ferro-muted ferro-package-history__empty" data-testid={`package-history-empty${suffix}`}>
          No days used yet.
        </p>
      ) : (
        <ul className="ferro-package-history__list" data-testid={`package-history-entries${suffix}`}>
          {entries.map((e) => (
            <li
              key={e.id}
              className={`ferro-package-history__row ferro-package-history__row--${e.kind}`}
              data-testid={`package-history-entry-${e.id}`}
            >
              <span className="ferro-package-history__delta">{entryFigure(e)}</span>
              <span className="ferro-package-history__what">
                <span>{describeEntry(e)}</span>
                {e.reason ? <span className="ferro-package-history__reason">“{e.reason}”</span> : null}
              </span>
              <span className="ferro-package-history__meta">
                {e.remainingBefore} → {e.remainingAfter}
                {e.staffUsername ? ` · ${e.staffUsername}` : ''}
                {' · '}
                {formatDay(e.createdAt.slice(0, 10))}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
